import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useBranch } from '../contexts/BranchContext';
import Layout from './Layout';
import LeadStagesManager from './LeadStagesManager';
import CustomerSegmentsModal from './CustomerSegmentsModal';
import EmailSettingsModal from './EmailSettingsModal';
import LeadSourcesManager from './LeadSourcesManager';
import TaskCategoryManager from './TaskCategoryManager';
import BranchManager from './BranchManager';
import RoleManagement from './RoleManagement';
import EmailTemplatesContent from './EmailTemplatesContent';
import { organizationAPI } from '../services/api';
import { BuildingOfficeIcon, GlobeAltIcon, BriefcaseIcon } from '@heroicons/react/24/outline';


const SETTINGS_TAB_IDS = ['users', 'leads', 'email', 'task-categories', 'branches', 'role-management'] as const;
type SettingsTabId = typeof SETTINGS_TAB_IDS[number];

const Settings: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { branches } = useBranch();
  const isAdmin = user?.role === 'admin' || user?.isOrgOwner;

  // Persist active tab in URL so it survives refresh
  const tabParam = searchParams.get('tab');
  const activeTab: SettingsTabId = (
    SETTINGS_TAB_IDS.includes(tabParam as SettingsTabId) &&
    (tabParam !== 'role-management' || isAdmin)
  ) ? (tabParam as SettingsTabId) : 'users';

  const setActiveTab = (tab: string) => {
    setSearchParams({ tab });
  };
  const [emailSubTab, setEmailSubTab] = useState<'smtp' | 'templates'>('smtp');
  const [members, setMembers] = useState<any>({ users: [], pendingInvitations: [] });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('agent');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showStagesManager, setShowStagesManager] = useState(false);
  const [showSegmentsModal, setShowSegmentsModal] = useState(false);
  const [showSourcesManager, setShowSourcesManager] = useState(false);
  const [showEmailSettingsModal, setShowEmailSettingsModal] = useState(false);
  
  // Branch assignment states
  const [inviteBranchAssignments, setInviteBranchAssignments] = useState<any[]>([]);
  
  // Module permissions states
  const [showModulePermissions, setShowModulePermissions] = useState(false);
  const [selectedBranchForPermissions, setSelectedBranchForPermissions] = useState<any>(null);
  const [modulePermissions, setModulePermissions] = useState<any[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  
  // User profile branch change states
  const [userBranchAssignments, setUserBranchAssignments] = useState<any[]>([]);
  const [availableBranches, setAvailableBranches] = useState<any[]>([]);
  const [showBranchChangeModal, setShowBranchChangeModal] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  
  // Admin user branch management states
  const [showUserBranchModal, setShowUserBranchModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [userBranchAssignmentsAdmin, setUserBranchAssignmentsAdmin] = useState<any[]>([]);
  const [isLoadingUserBranches, setIsLoadingUserBranches] = useState(false);

  // Business Settings: organization details (view/edit)
  const [organization, setOrganization] = useState<{ id: number; name: string; country: string | null; industry: string | null; logoUrl?: string | null; showLogoOnForms?: boolean } | null>(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgSaving, setOrgSaving] = useState(false);
  const [orgLogoUploading, setOrgLogoUploading] = useState(false);
  const [orgForm, setOrgForm] = useState({ name: '', country: '', industry: '' });
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isAdmin) {
      fetchMembers();
    } else {
      setIsLoading(false);
    }
    
    // Always fetch user's branch assignments and available branches
    fetchUserBranches();
    fetchAvailableBranches();
  }, [isAdmin]);

  // Fetch organization when Business Settings tab is active
  useEffect(() => {
    if (activeTab === 'branches') {
      fetchOrganization();
    }
  }, [activeTab]);

  const fetchOrganization = async () => {
    setOrgLoading(true);
    try {
      const res = await organizationAPI.get();
      if (res.success && res.organization) {
        const org = res.organization;
        setOrganization(org);
        setOrgForm({
          name: org.name || '',
          country: org.country ?? '',
          industry: org.industry ?? ''
        });
      }
    } catch (err) {
      console.error('Fetch organization error:', err);
      setError('Failed to load organization details');
    } finally {
      setOrgLoading(false);
    }
  };

  const saveOrganizationSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAdmin) return;
    if (!orgForm.name.trim()) {
      setError('Organization name is required');
      return;
    }
    setOrgSaving(true);
    setError('');
    try {
      const res = await organizationAPI.updateSettings({
        name: orgForm.name.trim(),
        country: orgForm.country.trim(),
        industry: orgForm.industry.trim()
      });
      if (res.success && res.organization) {
        setOrganization(res.organization);
        setOrgForm({
          name: res.organization.name || '',
          country: res.organization.country ?? '',
          industry: res.organization.industry ?? ''
        });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update organization');
    } finally {
      setOrgSaving(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !isAdmin) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      setError('Please choose a JPG, PNG, GIF, or WebP image.');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be 2MB or smaller.');
      return;
    }
    setOrgLogoUploading(true);
    setError('');
    try {
      const res = await organizationAPI.uploadLogo(file);
      if (res.organization) {
        setOrganization((prev) => prev ? { ...prev, logoUrl: res.organization.logoUrl } : prev);
      }
      if (logoInputRef.current) logoInputRef.current.value = '';
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to upload logo');
    } finally {
      setOrgLogoUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!isAdmin || !organization?.logoUrl) return;
    setOrgLogoUploading(true);
    setError('');
    try {
      const res = await organizationAPI.removeLogo();
      if (res.organization) {
        setOrganization((prev) => prev ? { ...prev, logoUrl: null } : prev);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to remove logo');
    } finally {
      setOrgLogoUploading(false);
    }
  };

  const fetchMembers = async () => {
    try {
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      const response = await fetch(`${API_BASE_URL}/invitations/members`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMembers(data.members);
      } else {
        setError('Failed to fetch team members');
      }
    } catch (error) {
      setError('Failed to fetch team members');
      console.error('Fetch members error:', error);
    } finally {
      setIsLoading(false);
    }
  };


  const fetchUserBranches = async () => {
    try {
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      const response = await fetch(`${API_BASE_URL}/user-profile/my-branches`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserBranchAssignments(data.branchAssignments);
      } else {
        console.error('Failed to fetch user branches');
      }
    } catch (error) {
      console.error('Fetch user branches error:', error);
    }
  };

  const fetchAvailableBranches = async () => {
    setIsLoadingBranches(true);
    try {
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      const response = await fetch(`${API_BASE_URL}/user-profile/available-branches`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableBranches(data.branches);
      } else {
        console.error('Failed to fetch available branches');
      }
    } catch (error) {
      console.error('Fetch available branches error:', error);
    } finally {
      setIsLoadingBranches(false);
    }
  };

  const handleInviteUser = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate branch assignments
    if (inviteBranchAssignments.length === 0) {
      setError('Please assign at least one branch to the user');
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      const response = await fetch(`${API_BASE_URL}/invitations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole,
          branchAssignments: inviteBranchAssignments,
          modulePermissions: modulePermissions // Include module permissions
        })
      });

      if (response.ok) {
        setShowInviteModal(false);
        setInviteEmail('');
        setInviteRole('agent');
        setInviteBranchAssignments([]);
        setModulePermissions([]);
        setShowModulePermissions(false);
        await fetchMembers(); // Refresh the list
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to send invitation');
      }
    } catch (error) {
      setError('Failed to send invitation');
      console.error('Invite error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfigurePermissions = async (branch: any) => {
    setSelectedBranchForPermissions(branch);
    setLoadingPermissions(true);
    
    try {
      // For new invitations, we'll create default full access permissions
      // In a real implementation, you might want to fetch existing permissions
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      
      // Get all modules
      const modulesResponse = await fetch(`${API_BASE_URL}/role-management/modules`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (modulesResponse.ok) {
        const modulesData = await modulesResponse.json();
        
        // Create default full access permissions for all modules
        const defaultPermissions = modulesData.modules.map((module: any) => ({
          moduleId: module.id,
          noAccess: false,
          canView: true,
          canCreate: true,
          canEdit: true,
          canDelete: true,
          fullAccess: true
        }));
        
        setModulePermissions(defaultPermissions);
        setShowModulePermissions(true);
      }
    } catch (error) {
      console.error('Error loading modules:', error);
      setError('Failed to load modules');
    } finally {
      setLoadingPermissions(false);
    }
  };

  const handlePermissionChange = (moduleId: number, permission: string, value: boolean) => {
    setModulePermissions(prev => 
      prev.map(module => {
        if (module.moduleId === moduleId) {
          const newPermissions = { ...module };
          
          if (permission === 'noAccess') {
            // If no access is selected, uncheck all others
            if (value) {
              newPermissions.noAccess = true;
              newPermissions.canView = false;
              newPermissions.canCreate = false;
              newPermissions.canEdit = false;
              newPermissions.canDelete = false;
              newPermissions.fullAccess = false;
            } else {
              newPermissions.noAccess = false;
            }
          } else if (permission === 'fullAccess') {
            // If full access is selected, uncheck no access
            if (value) {
              newPermissions.fullAccess = true;
              newPermissions.noAccess = false;
            } else {
              newPermissions.fullAccess = false;
            }
          } else {
            // For other permissions, uncheck no access and full access
            newPermissions[permission] = value;
            if (value) {
              newPermissions.noAccess = false;
              newPermissions.fullAccess = false;
            }
          }
          
          return newPermissions;
        }
        return module;
      })
    );
  };

  const getPermissionLabel = (permission: string) => {
    const labels: { [key: string]: string } = {
      noAccess: 'No Access',
      canView: 'View',
      canCreate: 'Create',
      canEdit: 'Edit',
      canDelete: 'Delete',
      fullAccess: 'Full Access'
    };
    return labels[permission] || permission;
  };

  const handleCancelInvitation = async (invitationId: string) => {
    if (!window.confirm('Are you sure you want to cancel this invitation?')) {
      return;
    }

    try {
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      const response = await fetch(`${API_BASE_URL}/invitations/${invitationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        await fetchMembers(); // Refresh the list
        alert('Invitation cancelled successfully');
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to cancel invitation');
      }
    } catch (error) {
      setError('Failed to cancel invitation');
      console.error('Cancel invitation error:', error);
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      const response = await fetch(`${API_BASE_URL}/invitations/${invitationId}/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Invitation email resent successfully!');
        await fetchMembers(); // Refresh to show updated expiration date
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to resend invitation');
      }
    } catch (error) {
      setError('Failed to resend invitation');
      console.error('Resend invitation error:', error);
    }
  };

  // Branch assignment functions
  const addBranchAssignment = () => {
    setInviteBranchAssignments([...inviteBranchAssignments, {
      branchId: '',
      branchRole: 'agent',
      isPrimaryBranch: inviteBranchAssignments.length === 0 // First assignment is primary by default
    }]);
  };

  const updateBranchAssignment = (index: number, field: string, value: any) => {
    const updatedAssignments = [...inviteBranchAssignments];
    updatedAssignments[index] = { ...updatedAssignments[index], [field]: value };
    
    // If setting as primary, unset other primary assignments
    if (field === 'isPrimaryBranch' && value) {
      updatedAssignments.forEach((assignment, i) => {
        if (i !== index) {
          assignment.isPrimaryBranch = false;
        }
      });
    }
    
    setInviteBranchAssignments(updatedAssignments);
  };

  const removeBranchAssignment = (index: number) => {
    const updatedAssignments = inviteBranchAssignments.filter((_, i) => i !== index);
    
    // If we removed the primary branch, make the first one primary
    if (updatedAssignments.length > 0 && !updatedAssignments.some(a => a.isPrimaryBranch)) {
      updatedAssignments[0].isPrimaryBranch = true;
    }
    
    setInviteBranchAssignments(updatedAssignments);
  };

  // User profile branch change functions
  const addUserBranchAssignment = () => {
    setUserBranchAssignments([...userBranchAssignments, {
      branchId: '',
      branchRole: 'agent',
      isPrimaryBranch: userBranchAssignments.length === 0
    }]);
  };

  const updateUserBranchAssignment = (index: number, field: string, value: any) => {
    const updatedAssignments = [...userBranchAssignments];
    updatedAssignments[index] = { ...updatedAssignments[index], [field]: value };
    
    // If setting as primary, unset other primary assignments
    if (field === 'isPrimaryBranch' && value) {
      updatedAssignments.forEach((assignment, i) => {
        if (i !== index) {
          assignment.isPrimaryBranch = false;
        }
      });
    }
    
    setUserBranchAssignments(updatedAssignments);
  };

  const removeUserBranchAssignment = (index: number) => {
    const updatedAssignments = userBranchAssignments.filter((_, i) => i !== index);
    
    // If we removed the primary branch, make the first one primary
    if (updatedAssignments.length > 0 && !updatedAssignments.some(a => a.isPrimaryBranch)) {
      updatedAssignments[0].isPrimaryBranch = true;
    }
    
    setUserBranchAssignments(updatedAssignments);
  };

  const handleBranchChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate confirmation text
    if (confirmationText !== 'confirm') {
      setError('Please type "confirm" to proceed with branch changes');
      setIsSubmitting(false);
      return;
    }

    // Validate branch assignments
    if (userBranchAssignments.length === 0) {
      setError('Please assign at least one branch');
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      const response = await fetch(`${API_BASE_URL}/user-profile/my-branches`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          branchAssignments: userBranchAssignments,
          confirmationText
        })
      });

      if (response.ok) {
        const data = await response.json();
        setUserBranchAssignments(data.branchAssignments);
        setShowBranchChangeModal(false);
        setConfirmationText('');
        setError('');
        // Refresh the page to update branch context
        window.location.reload();
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update branch assignments');
      }
    } catch (error) {
      setError('Failed to update branch assignments');
      console.error('Branch change error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Admin user branch management functions
  const fetchUserBranchesAdmin = async (userId: number) => {
    setIsLoadingUserBranches(true);
    try {
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      const response = await fetch(`${API_BASE_URL}/user-profile/user-branches/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUserBranchAssignmentsAdmin(data.branchAssignments);
      } else {
        console.error('Failed to fetch user branches');
        setUserBranchAssignmentsAdmin([]);
      }
    } catch (error) {
      console.error('Fetch user branches error:', error);
      setUserBranchAssignmentsAdmin([]);
    } finally {
      setIsLoadingUserBranches(false);
    }
  };

  const handleManageUserBranches = (user: any) => {
    setSelectedUser(user);
    setShowUserBranchModal(true);
    fetchUserBranchesAdmin(user.id);
  };

  const addUserBranchAssignmentAdmin = () => {
    setUserBranchAssignmentsAdmin([...userBranchAssignmentsAdmin, {
      branchId: '',
      branchRole: 'agent',
      isPrimaryBranch: userBranchAssignmentsAdmin.length === 0
    }]);
  };

  const updateUserBranchAssignmentAdmin = (index: number, field: string, value: any) => {
    const updatedAssignments = [...userBranchAssignmentsAdmin];
    updatedAssignments[index] = { ...updatedAssignments[index], [field]: value };
    
    // If setting as primary, unset other primary assignments
    if (field === 'isPrimaryBranch' && value) {
      updatedAssignments.forEach((assignment, i) => {
        if (i !== index) {
          assignment.isPrimaryBranch = false;
        }
      });
    }
    
    setUserBranchAssignmentsAdmin(updatedAssignments);
  };

  const removeUserBranchAssignmentAdmin = (index: number) => {
    const updatedAssignments = userBranchAssignmentsAdmin.filter((_, i) => i !== index);
    
    // If we removed the primary branch, make the first one primary
    if (updatedAssignments.length > 0 && !updatedAssignments.some(a => a.isPrimaryBranch)) {
      updatedAssignments[0].isPrimaryBranch = true;
    }
    
    setUserBranchAssignmentsAdmin(updatedAssignments);
  };

  const handleUpdateUserBranches = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    // Validate confirmation text
    if (confirmationText !== 'confirm') {
      setError('Please type "confirm" to proceed with branch changes');
      setIsSubmitting(false);
      return;
    }

    // Validate branch assignments
    if (userBranchAssignmentsAdmin.length === 0) {
      setError('Please assign at least one branch');
      setIsSubmitting(false);
      return;
    }

    try {
      const token = localStorage.getItem('leadshive_token');
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://139.59.160.11:5020/api';
      const response = await fetch(`${API_BASE_URL}/user-profile/user-branches/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          branchAssignments: userBranchAssignmentsAdmin,
          confirmationText
        })
      });

      if (response.ok) {
        const data = await response.json();
        setUserBranchAssignmentsAdmin(data.branchAssignments);
        setShowUserBranchModal(false);
        setConfirmationText('');
        setSelectedUser(null);
        setError('');
        await fetchMembers(); // Refresh the members list
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update branch assignments');
      }
    } catch (error) {
      setError('Failed to update branch assignments');
      console.error('User branch update error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };


  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'bg-gray-100 text-gray-800';
      case 'manager':
        return 'bg-gray-100 text-gray-700';
      case 'agent':
        return 'bg-gray-50 text-gray-600';
      case 'viewer':
        return 'bg-gray-50 text-gray-500';
      default:
        return 'bg-gray-50 text-gray-600';
    }
  };

  if (!isAdmin) {
    return (
      <Layout title="Settings" subtitle="Manage your LeadsHive settings">
        <div className="text-center py-8">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Access Denied</h3>
          <p className="text-gray-500">You need admin permissions to access settings.</p>
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout title="Settings" subtitle="Manage your LeadsHive settings">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Settings" subtitle="Manage your LeadsHive settings">
      <div className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-700">{error}</div>
          </div>
        )}

        {/* Settings Navigation */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('users')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'users'
                  ? 'border-gray-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              User Management
            </button>
            <button
              onClick={() => setActiveTab('leads')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'leads'
                  ? 'border-gray-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Leads Settings
            </button>
            <button
              onClick={() => {
                setActiveTab('email');
                setEmailSubTab('smtp');
              }}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'email'
                  ? 'border-gray-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Email Settings
            </button>
            <button
              onClick={() => setActiveTab('task-categories')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'task-categories'
                  ? 'border-gray-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Task Categories
            </button>
            <button
              onClick={() => setActiveTab('branches')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'branches'
                  ? 'border-gray-500 text-gray-900'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Business Settings
            </button>
            {isAdmin && (
              <button
                onClick={() => setActiveTab('role-management')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'role-management'
                    ? 'border-gray-500 text-gray-900'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Role Management
              </button>
            )}
          </nav>
        </div>

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Header with Action Buttons */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Team Members</h3>
                <p className="text-sm text-gray-500">
                  Manage users and their roles in your organization
                </p>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowInviteModal(true)}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md text-sm font-medium"
                >
                  Invite New User
                </button>
              </div>
            </div>

            {/* Active Users */}
            <div className="bg-white shadow overflow-hidden sm:rounded-lg">
              <div className="px-4 py-5 sm:px-6">
                <h4 className="text-md font-medium text-gray-900">Active Users</h4>
                <p className="text-sm text-gray-500">Users who have joined your organization</p>
              </div>
              <ul className="divide-y divide-gray-200">
                {members.users.map((member: any) => (
                  <li key={member.id} className="px-4 py-4 sm:px-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-10 w-10 bg-gray-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-medium">
                              {member.fullName?.charAt(0) || 'U'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="flex items-center">
                            <p className="text-sm font-medium text-gray-900">{member.fullName}</p>
                            {member.isOrgOwner && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Owner
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500">{member.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}
                        >
                          {member.role}
                        </span>
                        {member.lastLoginAt && (
                          <span className="text-xs text-gray-400">
                            Last login: {new Date(member.lastLoginAt).toLocaleDateString()}
                          </span>
                        )}
                        <button
                          onClick={() => handleManageUserBranches(member)}
                          className="ml-2 inline-flex items-center px-3 py-1 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          Manage Branches
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Pending Invitations */}
            {members.pendingInvitations.length > 0 && (
              <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                <div className="px-4 py-5 sm:px-6">
                  <h4 className="text-md font-medium text-gray-900">Pending Invitations</h4>
                  <p className="text-sm text-gray-500">Users who have been invited but haven't joined yet</p>
                </div>
                <ul className="divide-y divide-gray-200">
                  {members.pendingInvitations.map((invitation: any) => (
                    <li key={invitation.id} className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 bg-gray-300 rounded-full flex items-center justify-center">
                              <svg className="h-5 w-5 text-gray-500" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4">
                            <p className="text-sm font-medium text-gray-900">{invitation.email}</p>
                            <p className="text-sm text-gray-500">
                              Invited by {invitation.inviterName} • Expires {new Date(invitation.expiresAt).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <span
                            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(invitation.role)}`}
                          >
                            {invitation.role}
                          </span>
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                            Pending
                          </span>
                          <button
                            onClick={() => handleResendInvitation(invitation.id)}
                            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                          >
                            Resend
                          </button>
                          <button
                            onClick={() => handleCancelInvitation(invitation.id)}
                            className="text-red-600 hover:text-red-800 text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {/* Invite User Modal */}
        {showInviteModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-2xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Invite New User</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Send an email invitation to someone to join your organization.
                </p>
                <form onSubmit={handleInviteUser}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      value={inviteEmail}
                      onChange={(e) => setInviteEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                      placeholder="Enter email address"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Role
                    </label>
                    <select
                      value={inviteRole}
                      onChange={(e) => setInviteRole(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                    >
                      <option value="viewer">Viewer - Can view data only</option>
                      <option value="agent">Agent - Can manage leads</option>
                      <option value="manager">Manager - Can manage team and leads</option>
                      <option value="admin">Admin - Full access</option>
                    </select>
                  </div>

                  {/* Branch Assignments */}
                  <div className="mb-6">
                    <div className="flex justify-between items-center mb-3">
                      <label className="block text-sm font-medium text-gray-700">
                        Branch Assignments *
                      </label>
                      <button
                        type="button"
                        onClick={() => addBranchAssignment()}
                        className="text-sm text-blue-600 hover:text-blue-800"
                      >
                        + Add Branch
                      </button>
                    </div>
                    
                    {inviteBranchAssignments.length === 0 && (
                      <div className="text-sm text-gray-500 mb-3">
                        Click "Add Branch" to assign the user to specific branches
                      </div>
                    )}

                    {inviteBranchAssignments.map((assignment, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 mb-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Branch
                            </label>
                            <select
                              value={assignment.branchId}
                              onChange={(e) => updateBranchAssignment(index, 'branchId', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                              required
                            >
                              <option value="">Select a branch</option>
                              {branches.map((branch) => (
                                <option key={branch.id} value={branch.id}>
                                  {branch.name} {branch.isHeadOffice && '(HQ)'}
                                </option>
                              ))}
                            </select>
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Branch Role
                            </label>
                            <select
                              value={assignment.branchRole}
                              onChange={(e) => updateBranchAssignment(index, 'branchRole', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                            >
                              <option value="agent">Agent</option>
                              <option value="branch_manager">Branch Manager</option>
                              <option value="branch_admin">Branch Admin</option>
                              <option value="viewer">Viewer</option>
                            </select>
                          </div>
                        </div>

                        <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={assignment.isPrimaryBranch}
                              onChange={(e) => updateBranchAssignment(index, 'isPrimaryBranch', e.target.checked)}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Primary Branch</span>
                          </label>
                        </div>

                        <div className="mt-3 flex justify-between">
                          <button
                            type="button"
                            onClick={() => handleConfigurePermissions(assignment)}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            Configure Module Permissions
                          </button>
                          <button
                            type="button"
                            onClick={() => removeBranchAssignment(index)}
                            className="text-sm text-red-600 hover:text-red-800"
                          >
                            Remove Branch
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-end space-x-3">
                    <button
                      type="button"
                      onClick={() => {
                        setShowInviteModal(false);
                        setInviteBranchAssignments([]);
                      }}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 disabled:opacity-50"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Invitation'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Module Permissions Modal */}
        {showModulePermissions && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">Configure Module Permissions</h3>
                    <p className="text-sm text-gray-500">
                      Set module permissions for {selectedBranchForPermissions?.name || 'this branch'}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowModulePermissions(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {loadingPermissions ? (
                  <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="space-y-6 max-h-96 overflow-y-auto">
                    {modulePermissions.map((module) => (
                      <div key={module.moduleId} className="border border-gray-200 rounded-lg p-4">
                        <div className="mb-4">
                          <h4 className="text-sm font-medium text-gray-900">
                            {module.moduleDisplayName || module.moduleName}
                          </h4>
                          {module.description && (
                            <p className="text-xs text-gray-500 mt-1">{module.description}</p>
                          )}
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {['noAccess', 'canView', 'canCreate', 'canEdit', 'canDelete', 'fullAccess'].map((permission) => (
                            <div key={permission} className="flex items-start space-x-3">
                              <div className="flex items-center h-5">
                                <input
                                  type="checkbox"
                                  checked={module[permission] || false}
                                  onChange={(e) => handlePermissionChange(module.moduleId, permission, e.target.checked)}
                                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                              </div>
                              <div className="text-sm">
                                <label className="font-medium text-gray-700">
                                  {getPermissionLabel(permission)}
                                </label>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end space-x-3 mt-6">
                  <button
                    type="button"
                    onClick={() => setShowModulePermissions(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                  >
                    Done
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {/* Leads Settings Tab */}
        {activeTab === 'leads' && (
          <div className="space-y-6">
            {/* Customer Segments Management */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Customer Segments</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Create customer segments to categorize leads and enable segment-based pipelines.
                  </p>
                </div>
                <button
                  onClick={() => setShowSegmentsModal(true)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                >
                  Manage Segments
                </button>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4 bg-purple-50">
                <div className="flex items-center space-x-2 text-sm text-purple-800">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                  <span>Segments allow you to create different sales pipelines for different customer types. Assign stages to segments for dynamic filtering.</span>
                </div>
              </div>
            </div>

            {/* Lead Stages Management */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Lead Stages</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Manage your sales pipeline stages and assign them to customer segments.
                  </p>
                </div>
                <button
                  onClick={() => setShowStagesManager(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Manage Stages
                </button>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Click "Manage Stages" to customize your sales pipeline stages, assign them to segments, and set global stages for all leads.</span>
                </div>
              </div>
            </div>

            {/* Lead Sources Management */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Lead Sources</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Track where your leads are coming from and organize source attribution.
                  </p>
                </div>
                <button
                  onClick={() => setShowSourcesManager(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Manage Sources
                </button>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Click "Manage Sources" to add custom lead sources, track attribution, and see performance metrics.</span>
                </div>
              </div>
            </div>

            {/* Lead Form Template Management */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Lead Form Template</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Create custom lead form templates to control which fields are visible and required.
                  </p>
                </div>
                <button
                  onClick={() => navigate('/form-templates')}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                >
                  Manage Template
                </button>
              </div>
              
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Click "Manage Template" to create custom lead form templates, control field visibility, and set default templates.</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Email Settings Tab */}
        {activeTab === 'email' && (
          <div className="space-y-6">
            {/* Email Settings Sub-tabs */}
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                <button
                  onClick={() => setEmailSubTab('smtp')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    emailSubTab === 'smtp'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  SMTP Configuration
                </button>
                <button
                  onClick={() => setEmailSubTab('templates')}
                  className={`py-4 px-1 border-b-2 font-medium text-sm ${
                    emailSubTab === 'templates'
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  Email Templates
                </button>
              </nav>
            </div>

            {/* SMTP Configuration Sub-tab */}
            {emailSubTab === 'smtp' && (
              <div className="bg-white p-6 rounded-lg shadow border">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">SMTP Configuration</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Configure SMTP settings to enable email sending functionality across your organization.
                    </p>
                  </div>
                  <button
                    onClick={() => setShowEmailSettingsModal(true)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Manage SMTP Settings
                  </button>
                </div>

                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center space-x-2 text-sm text-gray-600">
                    <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Configure your SMTP server settings to send emails to leads. Support for Gmail, Outlook, Yahoo, and custom SMTP servers.</span>
                  </div>
                </div>
              </div>
            )}

            {/* Email Templates Sub-tab */}
            {emailSubTab === 'templates' && (
              <div>
                <EmailTemplatesContent />
              </div>
            )}
          </div>
        )}

        {/* Task Categories Tab */}
        {activeTab === 'task-categories' && (
          <div className="space-y-6">
            {/* Task Categories Management */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Task Categories</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Manage task categories to organize and categorize your tasks effectively.
                  </p>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span>Create and manage task categories to better organize your workflow. Categories help in filtering and grouping related tasks.</span>
                </div>
              </div>

              {/* Task Category Manager Component */}
              <div className="mt-6">
                <TaskCategoryManager />
              </div>
            </div>
          </div>
        )}

        {/* Business Settings Tab */}
        {activeTab === 'branches' && (
          <div className="space-y-6">
            {/* Organization Details */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <h3 className="text-lg font-medium text-gray-900 mb-1">Organization Details</h3>
              <p className="text-sm text-gray-600 mb-4">
                View and edit your organization name, country, industry, and logo. Only owners and admins can update these.
              </p>
              {orgLoading ? (
                <p className="text-sm text-gray-500">Loading organization...</p>
              ) : (
                <>
                  {/* Organization Logo */}
                  <div className="mb-6 pb-6 border-b border-gray-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Organization Logo</label>
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="w-24 h-24 rounded-lg border-2 border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {organization?.logoUrl ? (
                          <img src={organization.logoUrl} alt="Organization logo" className="w-full h-full object-contain" />
                        ) : (
                          <BuildingOfficeIcon className="w-12 h-12 text-gray-400" />
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <input
                          ref={logoInputRef}
                          type="file"
                          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                          onChange={handleLogoUpload}
                          className="hidden"
                        />
                        {isAdmin && (
                          <>
                            <button
                              type="button"
                              onClick={() => logoInputRef.current?.click()}
                              disabled={orgLogoUploading}
                              className="px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 border border-blue-600 rounded-md hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {orgLogoUploading ? 'Uploading...' : organization?.logoUrl ? 'Change logo' : 'Upload logo'}
                            </button>
                            {organization?.logoUrl && (
                              <button
                                type="button"
                                onClick={handleRemoveLogo}
                                disabled={orgLogoUploading}
                                className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:text-gray-800 border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Remove logo
                              </button>
                            )}
                          </>
                        )}
                        <p className="text-xs text-gray-500">JPG, PNG, GIF or WebP. Max 2MB.</p>
                      </div>
                    </div>
                  </div>

                  {/* Show logo on public forms */}
                  {isAdmin && (
                    <div className="mb-6 pb-6 border-b border-gray-200">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={organization?.showLogoOnForms ?? false}
                          onChange={async (e) => {
                            const checked = e.target.checked;
                            try {
                              await organizationAPI.updateSettings({ showLogoOnForms: checked });
                              setOrganization((prev) => prev ? { ...prev, showLogoOnForms: checked } : prev);
                            } catch (err: any) {
                              setError(err.response?.data?.error || 'Failed to update setting');
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm font-medium text-gray-700">
                          Show organization logo next to &quot;Organized by&quot; on public forms (when logo is uploaded)
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-1 ml-6">
                        If enabled and a logo is uploaded, the logo appears instead of the organization name next to &quot;Organized by&quot; on form pages.
                      </p>
                    </div>
                  )}

                <form onSubmit={saveOrganizationSettings} className="space-y-4 max-w-2xl">
                  <div>
                    <label htmlFor="org-name" className="block text-sm font-medium text-gray-700 mb-1">
                      Organization Name
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                        <BuildingOfficeIcon className="h-5 w-5" />
                      </div>
                      <input
                        id="org-name"
                        type="text"
                        value={orgForm.name}
                        onChange={(e) => setOrgForm((f) => ({ ...f, name: e.target.value }))}
                        disabled={!isAdmin}
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="Your organization name"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="org-country" className="block text-sm font-medium text-gray-700 mb-1">
                        Country
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                          <GlobeAltIcon className="h-5 w-5" />
                        </div>
                        <input
                          id="org-country"
                          type="text"
                          value={orgForm.country}
                          onChange={(e) => setOrgForm((f) => ({ ...f, country: e.target.value }))}
                          disabled={!isAdmin}
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="e.g. USA"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="org-industry" className="block text-sm font-medium text-gray-700 mb-1">
                        Industry
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                          <BriefcaseIcon className="h-5 w-5" />
                        </div>
                        <input
                          id="org-industry"
                          type="text"
                          value={orgForm.industry}
                          onChange={(e) => setOrgForm((f) => ({ ...f, industry: e.target.value }))}
                          disabled={!isAdmin}
                          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                          placeholder="e.g. Technology"
                        />
                      </div>
                    </div>
                  </div>
                  {isAdmin && (
                    <button
                      type="submit"
                      disabled={orgSaving}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {orgSaving ? 'Saving...' : 'Save changes'}
                    </button>
                  )}
                </form>
                </>
              )}
            </div>

            {/* Branch Management */}
            <div className="bg-white p-6 rounded-lg shadow border">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Branch Management</h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Create, edit, and manage branches for your organization. Each branch maintains its own data and settings.
                  </p>
                </div>
              </div>

              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span>Create new branches, edit existing ones, and manage branch details. Use the branch switcher in the sidebar to switch between branches.</span>
                </div>
              </div>

              {/* Branch Manager Component */}
              <div className="mt-6">
                <BranchManager />
              </div>
            </div>
          </div>
        )}

        {/* Role Management Tab */}
        {activeTab === 'role-management' && (
          <RoleManagement />
        )}
      </div>

      {/* Lead Stages Manager Modal */}
      {showStagesManager && (
        <LeadStagesManager
          onClose={() => setShowStagesManager(false)}
          onStageUpdate={() => {
            // Optionally refresh any data that depends on stages
            console.log('Stages updated');
          }}
        />
      )}

      {/* Customer Segments Modal */}
      <CustomerSegmentsModal
        open={showSegmentsModal}
        onClose={() => setShowSegmentsModal(false)}
      />

      {/* Lead Sources Manager Modal */}
      {showSourcesManager && (
        <LeadSourcesManager
          onClose={() => setShowSourcesManager(false)}
          onSourceUpdate={() => {
            // Optionally refresh any data that depends on sources
            console.log('Sources updated');
          }}
        />
      )}

      {/* Email Settings Modal */}
      {showEmailSettingsModal && (
        <EmailSettingsModal
          onClose={() => setShowEmailSettingsModal(false)}
          onSettingsUpdated={() => {
            console.log('Email settings updated');
          }}
        />
      )}

      {/* Branch Change Modal */}
      {showBranchChangeModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Change My Branch Assignments</h3>
              <p className="text-sm text-gray-500 mb-6">
                Update your branch assignments. You must type "confirm" to proceed with changes.
              </p>

              <form onSubmit={handleBranchChange}>
                {/* Branch Assignments */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Branch Assignments *
                    </label>
                    <button
                      type="button"
                      onClick={addUserBranchAssignment}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + Add Branch
                    </button>
                  </div>
                  
                  {userBranchAssignments.length === 0 && (
                    <div className="text-sm text-gray-500 mb-3">
                      Click "Add Branch" to assign yourself to specific branches
                    </div>
                  )}

                  {userBranchAssignments.map((assignment, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 mb-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Branch
                          </label>
                          <select
                            value={assignment.branchId}
                            onChange={(e) => updateUserBranchAssignment(index, 'branchId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                            required
                          >
                            <option value="">Select a branch</option>
                            {availableBranches.map((branch) => (
                              <option key={branch.id} value={branch.id}>
                                {branch.name} {branch.isHeadOffice && '(HQ)'}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Branch Role
                          </label>
                          <select
                            value={assignment.branchRole}
                            onChange={(e) => updateUserBranchAssignment(index, 'branchRole', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                          >
                            <option value="agent">Agent</option>
                            <option value="branch_manager">Branch Manager</option>
                            <option value="branch_admin">Branch Admin</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={assignment.isPrimaryBranch}
                            onChange={(e) => updateUserBranchAssignment(index, 'isPrimaryBranch', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">Primary Branch</span>
                        </label>
                      </div>

                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeUserBranchAssignment(index)}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove Branch
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Confirmation */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type "confirm" to proceed with branch changes
                  </label>
                  <input
                    type="text"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Type 'confirm' here"
                    required
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowBranchChangeModal(false);
                      setConfirmationText('');
                      setError('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Updating...' : 'Update Branch Assignments'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Admin User Branch Management Modal */}
      {showUserBranchModal && selectedUser && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Manage Branch Assignments for {selectedUser.fullName}
              </h3>
              <p className="text-sm text-gray-500 mb-6">
                Update branch assignments for this user. You must type "confirm" to proceed with changes.
              </p>

              <form onSubmit={handleUpdateUserBranches}>
                {/* Branch Assignments */}
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Branch Assignments *
                    </label>
                    <button
                      type="button"
                      onClick={addUserBranchAssignmentAdmin}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      + Add Branch
                    </button>
                  </div>
                  
                  {isLoadingUserBranches ? (
                    <div className="text-center py-4">
                      <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                      <p className="mt-2 text-sm text-gray-500">Loading user branches...</p>
                    </div>
                  ) : userBranchAssignmentsAdmin.length === 0 ? (
                    <div className="text-sm text-gray-500 mb-3">
                      Click "Add Branch" to assign this user to specific branches
                    </div>
                  ) : null}

                  {userBranchAssignmentsAdmin.map((assignment, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 mb-3">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Branch
                          </label>
                          <select
                            value={assignment.branchId}
                            onChange={(e) => updateUserBranchAssignmentAdmin(index, 'branchId', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                            required
                          >
                            <option value="">Select a branch</option>
                            {availableBranches.map((branch) => (
                              <option key={branch.id} value={branch.id}>
                                {branch.name} {branch.isHeadOffice && '(HQ)'}
                              </option>
                            ))}
                          </select>
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Branch Role
                          </label>
                          <select
                            value={assignment.branchRole}
                            onChange={(e) => updateUserBranchAssignmentAdmin(index, 'branchRole', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                          >
                            <option value="agent">Agent</option>
                            <option value="branch_manager">Branch Manager</option>
                            <option value="branch_admin">Branch Admin</option>
                            <option value="viewer">Viewer</option>
                          </select>
                        </div>
                      </div>

                      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <label className="flex items-center">
                          <input
                            type="checkbox"
                            checked={assignment.isPrimaryBranch}
                            onChange={(e) => updateUserBranchAssignmentAdmin(index, 'isPrimaryBranch', e.target.checked)}
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">Primary Branch</span>
                        </label>
                      </div>

                      <div className="mt-3 flex justify-end">
                        <button
                          type="button"
                          onClick={() => removeUserBranchAssignmentAdmin(index)}
                          className="text-sm text-red-600 hover:text-red-800"
                        >
                          Remove Branch
                        </button>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Confirmation */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type "confirm" to proceed with branch changes
                  </label>
                  <input
                    type="text"
                    value={confirmationText}
                    onChange={(e) => setConfirmationText(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    placeholder="Type 'confirm' here"
                    required
                  />
                </div>

                {/* Error Message */}
                {error && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowUserBranchModal(false);
                      setConfirmationText('');
                      setSelectedUser(null);
                      setUserBranchAssignmentsAdmin([]);
                      setError('');
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Updating...' : 'Update Branch Assignments'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default Settings;
